import { logError } from './sentryUtils';

const PRODUCTION_HOSTNAMES = [
    'fbm.misfitsmarket.com',
    'jalapeno.misfitsmarket.com',
    'lettuce.misfitsmarket.com',
    'metabolicmeals.misfitsmarket.com',
    'jalapeno.k8s-prod.misfitsmarket.io',
];
const LOCAL_DEV_HOSTS = ['localhost', '127.0.0.1'];
const PRODUCTION_DOMAINS = ['misfitsmarket.com', 'misfitsmarket.io'];
/**
 * Validates if an origin URL is from a trusted domain.
 *
 * Trusted domains:
 * - localhost (any port) for development
 * - *.misfitsmarket.com for production
 * - *.misfitsmarket.io for production
 *
 * @param {string} origin - The origin URL to validate (e.g., 'https://example.com')
 * @returns {boolean} true if the origin is trusted, false otherwise
 */
export const isTrustedOrigin = (origin) => {
    if (!origin) return false;

    try {
        const url = new URL(origin);
        const { hostname, protocol } = url;

        // Only allow http/https protocols
        if (protocol !== 'http:' && protocol !== 'https:') {
            return false;
        }

        // Allow localhost for development (any port)
        if (LOCAL_DEV_HOSTS.includes(hostname)) {
            return true;
        }

        // Check if hostname matches any production domain or their subdomains
        for (const domain of PRODUCTION_DOMAINS) {
            if (hostname === domain || hostname.endsWith(`.${domain}`)) {
                return true;
            }
        }

        return false;
    } catch (e) {
        logError(e);
        return false;
    }
};

/**
 * Determines if the current environment is local development.
 *
 * @param {string} hostname - The hostname to check (defaults to window.location.host)
 * @returns {boolean} true if running on localhost or 127.0.0.1
 */
export const isLocalDev = (hostname = window.location.host) => {
    const host = hostname.split(':')[0]; // Remove port if present
    return LOCAL_DEV_HOSTS.includes(host);
};

/**
 * Determines if the current environment is production.
 * Production is identified by known production hostnames.
 *
 * @param {string} hostname - The hostname to check (defaults to window.location.host)
 * @returns {boolean} true if running on a production hostname
 */
export const isProduction = (hostname = window.location.host) => {
    const host = hostname.split(':')[0]; // Remove port if present
    return PRODUCTION_HOSTNAMES.includes(host);
};

/**
 * Gets the environment name and subdomain from the current hostname.
 *
 * @param {string} hostname - The hostname to parse (defaults to window.location.host)
 * @returns {Object} Object containing env (environment name) and subdomain (if any)
 *   - env: 'development' for localhost, 'production' for production hostnames, or the subdomain name
 *   - subdomain: null for localhost, or the subdomain string
 */
export const getEnvironmentInfo = (hostname = window.location.host) => {
    const host = hostname.split(':')[0]; // Remove port if present

    // Check for localhost
    if (LOCAL_DEV_HOSTS.includes(host)) {
        return {
            env: 'development',
            subdomain: null,
        };
    }

    // Check for misfitsmarket.com or misfitsmarket.io subdomain
    const domainPattern = PRODUCTION_DOMAINS.map((domain) => domain.replace('.', '\\.')).join('|');
    const subdomainMatch = host.match(new RegExp(`^(.+)\\.(${domainPattern})$`));

    if (subdomainMatch) {
        const [, subdomain] = subdomainMatch;

        if (PRODUCTION_HOSTNAMES.includes(host)) {
            return {
                env: 'production',
                subdomain,
            };
        }

        // Other subdomains (dev environments)
        return {
            env: subdomain,
            subdomain,
        };
    }

    // Default to development if no match
    return {
        env: 'development',
        subdomain: null,
    };
};

/**
 * Determines whether the first subdomain label is a quince environment.
 *
 * Examples:
 * - quince.misfitsmarket.com => true
 * - quince-dev.misfitsmarket.com => true
 * - lettuce.quince.misfitsmarket.com => false
 * - VITE_REACT_APP_FORCE_QUINCE=true => true for any hostname
 *
 * @param {string} hostname - The hostname to check (defaults to window.location.host)
 * @returns {boolean} true when the first subdomain label contains 'quince'
 */
export const isQuince = (hostname = window.location.host) => {
    const forceQuince = `${import.meta.env?.VITE_REACT_APP_FORCE_QUINCE ?? ''}`.trim().toLowerCase() === 'true';
    if (forceQuince) {
        return true;
    }

    const host = hostname.split(':')[0];
    const [subdomainLabel = ''] = host.split('.');
    return subdomainLabel.includes('quince');
};
